/**
 * errorCodes.js — 稳定错误码契约（Ports 层，单一事实源）。
 *
 * ADR-004 归一契约中 { ok, error.code } 的 code 取值枚举。
 * 之前该枚举定义在 @/api/apiResultAdapter，导致 Application/Domain 若要判定
 * error.code 语义必须反向依赖 api 层具体物（违反依赖矩阵：Application 不得 import @/api）。
 * 现把枚举提到稳定契约层：api adapter 从此处 re-export，Application 依赖契约而非 @/api。
 * 不新增/删除任何 code；与原 apiResultAdapter.ERROR_CODES 逐字等价。
 */
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  MOCK_NOT_FOUND: 'MOCK_NOT_FOUND',
  STORAGE_READ_FAILED: 'STORAGE_READ_FAILED',
  STORAGE_WRITE_FAILED: 'STORAGE_WRITE_FAILED',
  EXPORT_FAILED: 'EXPORT_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  // 新增：AI 设计集成错误码
  REMOTE_SERVICE_ERROR: 'REMOTE_SERVICE_ERROR',  // 后端 5xx 或 status != SUCCESS
  TIMEOUT: 'TIMEOUT',                             // 请求超时
  PARSE_ERROR: 'PARSE_ERROR',                     // zip 损坏或 JSON 解析失败
  PARTITION_FAILED: 'PARTITION_FAILED',           // 粗划分平铺完整性不满足
  OUTLINE_SHRUNK: 'OUTLINE_SHRUNK',               // 外轮廓被削减（保留兼容，新流程用 PREVIEW_REQUIRED）
  // T01-2: 追加四个新错误码（只追加，不删除或改变既有码值）
  AI_RESULT_INVALID: 'AI_RESULT_INVALID',                     // AI 返回缺层、空几何、未知门窗等无效结构
  AI_RESULT_STATS_MISMATCH: 'AI_RESULT_STATS_MISMATCH',       // 后端统计与实际门窗不一致
  AI_ELEMENT_GEOMETRY_CHANGED: 'AI_ELEMENT_GEOMETRY_CHANGED', // 门窗阶段越权修改了墙/房间
  AI_SOURCE_SCENE_CHANGED: 'AI_SOURCE_SCENE_CHANGED',        // 生成期间当前户型已经变化
  // F01（0805 异步 AI 生成）：只在末尾追加，既有码值逐字不变、不删不改。
  AI_TASK_BUSY: 'AI_TASK_BUSY',                       // 活动户型任务达到两任务软上限
  AI_TASK_ALREADY_ACTIVE: 'AI_TASK_ALREADY_ACTIVE',   // 同项目已有未放弃的活动户型任务
  AI_TASK_ABANDONED: 'AI_TASK_ABANDONED',             // 任务已被用户放弃（A02 ABANDONED 门禁使用）
  AI_ATTEMPT_INVALID: 'AI_ATTEMPT_INVALID',           // 非法后台状态，不写入 Registry
  AI_ATTEMPT_NOT_FOUND: 'AI_ATTEMPT_NOT_FOUND',       // 后台 404 找不到 Attempt
  AI_RESULT_NOT_READY: 'AI_RESULT_NOT_READY',         // 结果尚未就绪（下载 409）
  APPLY_FAILED: 'APPLY_FAILED',                       // 应用结果（版本/保存/加载）失败
  UNKNOWN: 'UNKNOWN',                                 // 无法归类的兜底错误
  AI_STRUCTURE_NO_FEASIBLE_SOLUTION: 'AI_STRUCTURE_NO_FEASIBLE_SOLUTION',
  AI_STRUCTURE_EMPTY_MODEL: 'AI_STRUCTURE_EMPTY_MODEL',
  AI_STRUCTURE_INVALID_MODEL: 'AI_STRUCTURE_INVALID_MODEL',
  AI_STRUCTURE_LAYER_MISMATCH: 'AI_STRUCTURE_LAYER_MISMATCH',
  AI_STRUCTURE_HOST_WALL_NOT_FOUND: 'AI_STRUCTURE_HOST_WALL_NOT_FOUND',
  AI_STRUCTURE_HOST_WALL_AMBIGUOUS: 'AI_STRUCTURE_HOST_WALL_AMBIGUOUS',
  AI_STRUCTURE_SOURCE_CHANGED: 'AI_STRUCTURE_SOURCE_CHANGED',
  AI_STRUCTURE_RESULT_VERSION_MISMATCH: 'AI_STRUCTURE_RESULT_VERSION_MISMATCH',
  AI_STRUCTURE_TASK_NOT_FOUND: 'AI_STRUCTURE_TASK_NOT_FOUND',
  // Development_0815（图纸矢量化接入）：只在末尾追加，既有码值逐字不变、不删不改。
  DRAWING_VECTORIZE_BUSY: 'DRAWING_VECTORIZE_BUSY',         // 后端并发闸门已满（HTTP 429），带 retryAfterSec
  DRAWING_VECTORIZE_FAILED: 'DRAWING_VECTORIZE_FAILED',     // 识别失败（模型/网关全挂）
  DRAWING_INPUT_UNSUPPORTED: 'DRAWING_INPUT_UNSUPPORTED',   // 文件类型/尺寸/页码不合法
  DRAWING_CALIBRATION_INVALID: 'DRAWING_CALIBRATION_INVALID', // 参考线过短、长度单位错、尺度量级离谱
  DRAWING_SCENE_REJECTED: 'DRAWING_SCENE_REJECTED',         // 识别出的 scene 被兼容管线拒绝
  // 识别改走异步作业（提交 → 轮询 → 取结果）后追加。同样只追加，不删不改。
  // 注意 DRAWING_VECTORIZE_BUSY 保留但识别路径不再产生它：异步侧没有信号量闸门。
  DRAWING_JOB_SUBMIT_FAILED: 'DRAWING_JOB_SUBMIT_FAILED',   // 上传图纸或创建作业失败
  DRAWING_JOB_NOT_FOUND: 'DRAWING_JOB_NOT_FOUND',           // 轮询时后端查不到这个作业
  DRAWING_JOB_FAILED: 'DRAWING_JOB_FAILED',                 // 作业跑到终态 FAILED / CANCELED
  DRAWING_JOB_TIMEOUT: 'DRAWING_JOB_TIMEOUT',               // 轮询超过上限仍未终态
  DRAWING_RESULT_NOT_READY: 'DRAWING_RESULT_NOT_READY'      // 作业已 SUCCESS 但结果表里还没有记录
}

/**
 * DRAWING_VECTORIZE_STAGE — 图纸矢量化的失败阶段（AppError.stage 取值）。
 * 与 ERROR_CODES 正交：同一 code 可能出现在不同 stage。
 */
export const DRAWING_VECTORIZE_STAGE = {
  ESTIMATE: 'estimate',
  EXTRACT: 'extract',
  CALIBRATE: 'calibrate',
  // 识别拆成异步四步之后细分出来的阶段。`EXTRACT` 保留：Application 层把
  // 归一后的 payload 仍标成 extract，UI 不必关心它是同步还是异步来的。
  UPLOAD: 'upload',
  SUBMIT: 'submit',
  POLL: 'poll',
  RESULT: 'result'
}

/**
 * AI_DESIGN_STAGE — 异步 AI 生成流程阶段枚举（AppError.stage 取值）。
 *
 * 稳定契约层枚举，供 Domain/Application/Adapter 标注错误发生的阶段，
 * 与 ERROR_CODES 正交：同一 code 可能出现在不同 stage。
 */
export const AI_DESIGN_STAGE = {
  VALIDATE: 'VALIDATE',
  CAPACITY: 'CAPACITY',
  UPLOAD: 'UPLOAD',
  CREATE_ATTEMPT: 'CREATE_ATTEMPT',
  POLL: 'POLL',
  QUERY_RESULT: 'QUERY_RESULT',
  DOWNLOAD: 'DOWNLOAD',
  UNZIP: 'UNZIP',
  REPAIR: 'REPAIR',
  MIGRATION: 'MIGRATION',
  INVARIANT: 'INVARIANT',
  APPLY: 'APPLY',
  STORAGE: 'STORAGE'
}
